import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  message,
  Space,
  Tag,
} from 'antd';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FinancialService } from '../../service/financial-service/financial.service';
import {
  FinancialRecordRequestDto,
  FinancialRecordType,
} from '../../model/Financial.model';
import useEmployee from '../../hook/useEmployee';
import useRole from '../../hook/useRole';

const { Option } = Select;
const { TextArea } = Input;

interface CreateFinancialRecordModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateFinancialRecordModal: React.FC<CreateFinancialRecordModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { employee } = useEmployee();
  const role = useRole();

  const categoryOptions = {
    [FinancialRecordType.INCOME]: [
      'Thanh toán từ khách hàng',
      'Lãi ngân hàng',
      'Thu khác',
      'Hoàn tiền',
    ],
    [FinancialRecordType.EXPENSE]: [
      'Hoa hồng',
      'Khách hàng',
      'Tiền thuê văn phòng',
      'Lương nhân viên',
      'Chi phí vận hành',
      'Chi phí marketing',
      'Chi phí thiết bị',
      'Chi khác',
    ],
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!employee) {
        message.error('Không thể xác định thông tin người dùng');
        return;
      }

      const requestData: FinancialRecordRequestDto = {
        createdByEmployeeId: employee.id, // Truyền employee ID để backend biết ai tạo
        recordType: values.recordType,
        category: values.category,
        amount: values.amount,
        recordDate: values.recordDate
          ? values.recordDate.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        source: values.source,
        description: values.description,
        referenceNumber: values.referenceNumber,
      };

      await FinancialService.createRecord(requestData);
      message.success('Tạo bản ghi thu chi thành công!');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating record:', error);
      message.error(error.response?.data || 'Có lỗi xảy ra khi tạo bản ghi');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const selectedRecordType = Form.useWatch('recordType', form);

  const getRoleColor = (role: string | null) => {
    switch (role) {
      case 'ROLE_ADMIN':
        return 'red';
      case 'ROLE_EMPLOYEE':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getRoleText = (role: string | null) => {
    switch (role) {
      case 'ROLE_ADMIN':
        return 'Admin';
      case 'ROLE_EMPLOYEE':
        return 'Nhân viên';
      default:
        return 'Không xác định';
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center justify-between">
          <span>Tạo bản ghi thu chi</span>
          {employee && role && (
            <Tag icon={<UserOutlined />} color={getRoleColor(role)}>
              {employee.name} ({getRoleText(role)})
            </Tag>
          )}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={500}
      footer={[
        <Button key="cancel" onClick={handleCancel} size="small">
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={handleSubmit}
          disabled={!employee}
          size="small"
        >
          Tạo bản ghi
        </Button>,
      ]}
    >
      {employee && role && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <div className="flex justify-between items-center text-blue-800">
            <span>
              <UserOutlined className="mr-1" />
              {employee.name} ({getRoleText(role)})
            </span>
            <span className="text-blue-600">
              {role === 'ROLE_ADMIN' ? 'AccountBalance' : 'EmployeeFund'}
            </span>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          recordDate: dayjs(),
          recordType: FinancialRecordType.INCOME,
        }}
        size="small"
      >
        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="recordType"
            label="Loại giao dịch"
            rules={[{ required: true, message: 'Chọn loại' }]}
          >
            <Select placeholder="Chọn loại" size="small">
              <Option value={FinancialRecordType.INCOME}>
                <span style={{ color: '#52c41a' }}>Thu</span>
              </Option>
              <Option value={FinancialRecordType.EXPENSE}>
                <span style={{ color: '#f5222d' }}>Chi</span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item name="category" label="Danh mục">
            <Select placeholder="Chọn danh mục" allowClear size="small">
              {selectedRecordType &&
                categoryOptions[selectedRecordType as FinancialRecordType]?.map(
                  (category: string) => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  )
                )}
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="amount"
            label="Số tiền"
            rules={[
              { required: true, message: 'Nhập số tiền' },
              { type: 'number', min: 0.01, message: 'Số tiền > 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Nhập số tiền"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value!.replace(/,/g, '')}
              addonAfter="VND"
              size="small"
            />
          </Form.Item>

          <Form.Item name="recordDate" label="Ngày giao dịch">
            <DatePicker
              style={{ width: '100%' }}
              showTime
              placeholder="Chọn ngày"
              format="DD/MM/YYYY HH:mm"
              size="small"
            />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Mô tả" className="mb-3">
          <TextArea placeholder="Nhập mô tả chi tiết" rows={2} size="small" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="source" label="Nguồn">
            <Input placeholder="Nguồn thu/chi" size="small" />
          </Form.Item>

          <Form.Item name="referenceNumber" label="Số tham chiếu">
            <Input placeholder="Số hóa đơn, mã GD..." size="small" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateFinancialRecordModal;
